import includes from 'lodash/includes'
import map from 'lodash/map'
import React, { FC, ReactNode, useCallback, useContext, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

import SVGClose from '@Assets/SVG/Close'
import ArrowPagination from '@Components/Product/Slider/Components/ArrowPagination/ArrowPagination'
import { ImageHorizontal } from '@Components/Product/Slider/Components/Image/ImageHorizontal'
import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { DragNext } from '@Components/Product/Slider/gestures/dragNext'
import { VideoHorizontal } from '@Components/Product/Slider/Video'
import { blackImageOverlay, buttonReset } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

const SWIPE_UP_ANIMATION_DURATION = 500

const ExitZoom = styled.button`
  display: flex;
  position: fixed;
  top: 6px;
  right: 56px;
  margin-top: 4px;
  z-index: 1001;
  cursor: pointer;
  background: rgba(245, 245, 245, .6) !important;
  padding: 8.5px 8px 9px 8px;
  margin-left: 8px;
  border: none;

  &:hover {
    background: rgba(180, 180, 180, 0.5);
  }
`

const HorizontalSliderRoot = styled.div<{ fullscreen: boolean, swipe: boolean }>(({
  fullscreen,
  swipe,
}) => css`
  position: relative;
  overflow: hidden;
  max-height: 586px;
  opacity: 1;
  transform: none;

  ${fullscreen && css`
    background: white;
    position: fixed;
    z-index: 9000;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: unset;

    ${swipe && css`
      animation: slide-up-opacity cubic-bezier(0.4, 0, 0.2, 1) forwards;
      animation-duration: ${SWIPE_UP_ANIMATION_DURATION}ms;

      @keyframes slide-up-opacity {
        0% {
          transform: translateY(0%);
          opacity: 1;
        }
        100% {
          transform: translateY(-100%);
          opacity: 0;
        }
      }
    `}
  `};

  ${blackImageOverlay};
`)

export const CloseButton = styled.button`
  ${buttonReset};
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(245, 245, 245, .6);
  padding: 13px;
  height: 40px;
  width: 40px;

  > svg {
    width: 14px;
    height: 14px;
  }

  > svg:hover {
    transform: scale(1.2);
    transition-duration: .05s;
  }
`

interface HorizontalSliderProps {
  children?: ReactNode
}

const HorizontalSlider: FC<HorizontalSliderProps> = ({ children }) => {
  const { t } = useTranslation()
  const {
    disableDrag,
    isDragDisabled,
    isZoomedIn,
    setZoomedIn,
  } = useContext(ControllerContext)
  const {
    activeSlide,
    goToNextSlide,
    goToPreviousSlide,
    isFullscreen,
    isSwipedUp,
    setSwipeUp,
    slides,
    toggleFullscreen,
  } = useContext(SliderContext)
  const horizontalRef = useRef<HTMLUListElement>(null)
  DragNext(horizontalRef, goToNextSlide, goToPreviousSlide, 'x')

  const updateToggles = useCallback(() => {
    if (isSwipedUp) {
      const html = (document.getElementsByTagName('BODY') as HTMLCollectionOf<HTMLElement>)[0]
      html.style.overflow = ''
      html.style.position = ''

      setTimeout(() => {
        setSwipeUp(false)
        disableDrag(false)
        toggleFullscreen()
      }, SWIPE_UP_ANIMATION_DURATION)
    }
  }, [disableDrag, isSwipedUp, setSwipeUp, toggleFullscreen])

  useEffect(() => {
    updateToggles()
  }, [isSwipedUp, updateToggles])

  const touchAction = isFullscreen ? 'none' : 'pan-y'

  const content = ref => (
    <>
      {children}

      {!isDragDisabled && (
        <ArrowPagination
          onClickLeft={() => goToPreviousSlide(true)}
          onClickRight={() => goToNextSlide(true)}
        />
      )}
      <span css={{ paddingTop: '100%', display: 'block' }} />
      <ul
        css={{
          display: 'flex',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          listStyle: 'none',
          padding: 0,
          margin: 0,
          touchAction,
          '-webkit-touch-action': touchAction,
        }}
        ref={ref}
        style={{
          transform: `translateX(-${activeSlide.key * 100}%)`,
          transition: activeSlide.recalculate ? 'transform .45s cubic-bezier(.215,.61,.355,1)' : '',
        }}
      >
        {map(slides, (slide, key) => (
          <li
            key={`${slide.url}-${key}`}
            css={{
              display: 'flex',
              flexShrink: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            {
              includes(slide.tags, 'video')
                ? (
                  <VideoHorizontal
                    asset={slide}
                    isActive={activeSlide.key === key}
                    isFullscreen={false}
                  />
                )
                : <ImageHorizontal asset={slide} index={key} title="TODO" />
            }
          </li>
        ))}
      </ul>
      {isZoomedIn
        && <ExitZoom onClick={() => setZoomedIn(false)}>{t('common:ZOOM_OUT')}</ExitZoom>}
      {!isSwipedUp && isFullscreen && (
        <CloseButton onClick={toggleFullscreen}>
          <SVGClose />
        </CloseButton>
      )}
    </>
  )

  return (
    <>
      <HorizontalSliderRoot
        fullscreen={isFullscreen}
        swipe={isSwipedUp}
      >
        {content(horizontalRef)}
      </HorizontalSliderRoot>
      {isSwipedUp && (
        <HorizontalSliderRoot fullscreen={false} swipe={false}>
          {content(null)}
        </HorizontalSliderRoot>
      )}
    </>
  )
}

export default HorizontalSlider
